import React, { useState, useEffect } from "react";
import axios from "axios";
import { View, Text, TextInput, ActivityIndicator, FlatList, TouchableOpacity, Modal } from "react-native-web";
import { FaMicrophone, FaMicrophoneSlash } from "react-icons/fa";
import { IoMdClose, IoMdChatbubbles } from "react-icons/io";
import styles from "./GeminiChatStyle";

const GeminiChat = () => {
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showChatbot, setShowChatbot] = useState(false);
  const [voices, setVoices] = useState([]);

  useEffect(() => {
    const savedMessages = JSON.parse(localStorage.getItem('chatMessages')) || [];
    const lastSavedTimestamp = localStorage.getItem('lastSavedTimestamp');

    if (savedMessages.length > 0 && lastSavedTimestamp) {
      const currentTime = new Date().getTime();
      const timeDifference = currentTime - new Date(lastSavedTimestamp).getTime();

      // Clear messages if more than 1 day (24 hours) has passed
      if (timeDifference > 24 * 60 * 60 * 1000) {
        localStorage.removeItem('chatMessages');
        localStorage.removeItem('lastSavedTimestamp');
      } else {
        setMessages(savedMessages);
      }
    }

    if (savedMessages.length === 0) {
      const startChat = async () => {
        const initialMessage = await fetchResponse("Hello!");
        if (!initialMessage.includes("Error") && !initialMessage.includes("Sorry")) {
          const updatedMessages = [{ text: initialMessage, user: false }];
          setMessages(updatedMessages);
          localStorage.setItem('chatMessages', JSON.stringify(updatedMessages));
          localStorage.setItem('lastSavedTimestamp', new Date().toISOString());
        }
      };
      startChat();
    }
  }, []);

  useEffect(() => {
    const loadVoices = () => {
      const synth = window.speechSynthesis;
      if (synth.onvoiceschanged !== undefined) {
        synth.onvoiceschanged = () => {
          setVoices(synth.getVoices());
        };
      }
      setVoices(synth.getVoices());
    };

    loadVoices();
  }, []);

  const fetchResponse = async (prompt) => {
    try {
      const response = await axios.post(
        `https://backend-nu-two.vercel.app/chat`, // Change to your backend endpoint
        { userInput: prompt },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data && response.data.response) {
        return response.data.response;
      }
      return "No valid response found.";
    } catch (error) {
      return "Sorry, there was an error fetching the response.";
    }
  };

  const sendMessage = async () => {
    if (userInput.trim() === "") return;
    setLoading(true);
    const userMessage = { text: userInput, user: true };
    const updatedMessages = [userMessage, ...messages];
    setMessages(updatedMessages);

    localStorage.setItem('chatMessages', JSON.stringify(updatedMessages));
    localStorage.setItem('lastSavedTimestamp', new Date().toISOString());

    const conversationHistory = updatedMessages
      .slice(0, 10) // Send the last 10 messages for context
      .reverse()
      .map(msg => msg.user ? `User: ${msg.text}` : `Bot: ${msg.text}`)
      .join("\n");

    const botResponse = await fetchResponse(conversationHistory + `\nUser: ${userInput}`);
    if (!botResponse.includes("Error") && !botResponse.includes("Sorry")) {
      const botMessage = { text: botResponse, user: false };
      const updatedMessagesWithBot = [botMessage, ...updatedMessages];
      setMessages(updatedMessagesWithBot);

      localStorage.setItem('chatMessages', JSON.stringify(updatedMessagesWithBot));
      localStorage.setItem('lastSavedTimestamp', new Date().toISOString());
    }
    setLoading(false);
    setUserInput("");
  };

  const renderText = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <Text key={index} style={{ fontWeight: 'bold' }}>{part.slice(2, -2)}</Text>;
      } else {
        return <Text key={index}>{part}</Text>;
      }
    });
  };

  const renderMessage = ({ item }) => (
    <View style={[styles.messageContainer, item.user ? styles.userMessageContainer : styles.botMessageContainer]}>
      {renderText(item.text)}
    </View>
  );

  const handleSpeech = () => {
    if (!isSpeaking) {
      const lastMessage = messages[0]?.text || "";
      sing(stripMarkdown(lastMessage));
    } else {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  const stripMarkdown = (text) => {
    return text.replace(/\*\*/g, '');
  };

  const sing = (text) => {
    if (text && window.speechSynthesis) {
      const utterance = new SpeechSynthesisUtterance();
      const selectedVoice = voices.find(voice => voice.name.includes('UK English Male')) || voices[0];
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);

      const lines = text.split(/[\r\n]+/);
      let currentLine = 0;

      const singLine = () => {
        if (currentLine < lines.length) {
          utterance.text = lines[currentLine];
          utterance.pitch = 1 + (currentLine % 2) * 0.5; // Alternate pitch for effect
          utterance.rate = 1 + (currentLine % 2) * 0.2; // Alternate rate for effect
          utterance.onend = () => {
            currentLine++;
            singLine();
          };
          window.speechSynthesis.speak(utterance);
        } else {
          setIsSpeaking(false);
        }
      };

      setIsSpeaking(true);
      singLine();
    }
  };

  return (
    <View style={styles.mainContainer}>
      <TouchableOpacity style={styles.chatButton} onPress={() => setShowChatbot(true)}>
        <IoMdChatbubbles size={24} color="#fff" />
      </TouchableOpacity>

      <Modal
        animationType="slide"
        transparent={true}
        visible={showChatbot}
        onRequestClose={() => setShowChatbot(false)}
      >
        <View style={styles.modalOverlay} />
        <View style={styles.modalContainer}>
          <View style={styles.chatContainer}>
            <View style={styles.header}>
              <Text style={styles.headerTitle}>GIT Bot</Text>
              <TouchableOpacity onPress={() => setShowChatbot(false)}>
                <IoMdClose size={24} color="#fff" />
              </TouchableOpacity>
            </View>
            <FlatList
              data={messages}
              renderItem={renderMessage}
              keyExtractor={(item, index) => index.toString()}
              contentContainerStyle={styles.listContainer}
              inverted
            />
            <View style={styles.inputContainer}>
              <TouchableOpacity style={styles.micIcon} onPress={handleSpeech}>
                {isSpeaking ? <FaMicrophoneSlash size={24} color="white" /> : <FaMicrophone size={24} color="white" />}
              </TouchableOpacity>
              <TextInput
                placeholder="Type a message..."
                onChangeText={setUserInput}
                value={userInput}
                style={styles.input}
                onSubmitEditing={sendMessage}
              />
            </View>
            {loading && <ActivityIndicator size="large" color="#007bff" style={styles.loading} />}
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default GeminiChat;